/**
 * Function problems, the signature - `EX-FNC04`, Wave 36, `docs/27` §9.41
 * (the engine is `EX-FNC03`, ADR-0188).
 *
 * A function problem asks for one function, LeetCode's way. The setter writes
 * its signature (a name, one to six typed parameters and a return type); the
 * starters and the jury's grader are generated from it, and absent it the
 * problem is a whole program, as every problem before Wave 36 is.
 *
 * Three ports of the server live here, each tested on the server's own data:
 * the naming rules (`function-signature.ts`, `readSignature`), so a field goes
 * red on exactly what the save would refuse; and the five starter generators
 * (`function-generators/`), so the setter sees every language's starter as
 * they type, tested against the server's golden files. The graders are never
 * ported: the setter reads them from the saved problem (`graderPreview`).
 *
 * As in `function-value.ts`, a refusal is a code, so the page says it in Bangla.
 */

export const FUNCTION_VALUE_TYPES = [
  "int",
  "long",
  "double",
  "bool",
  "char",
  "string",
  "int[]",
  "long[]",
  "double[]",
  "bool[]",
  "char[]",
  "string[]",
  "int[][]",
  "char[][]",
] as const;
export type FunctionValueType = (typeof FUNCTION_VALUE_TYPES)[number];

export const SIGNATURE_PARAMS_MAX = 6;
/** A double answer's tolerance when the setter names none, absolute or relative. */
export const SIGNATURE_TOLERANCE_DEFAULT = 1e-6;

export type FunctionSignatureParam = { name: string; type: FunctionValueType };

/** As the server stores and sends it: `unordered` only when true, `tolerance` only when set. */
export type FunctionSignature = {
  name: string;
  params: FunctionSignatureParam[];
  returns: FunctionValueType;
  unordered?: true;
  tolerance?: number;
};

/** The languages a function problem offers (call 6), in the order the setter's tabs show them. */
export const FUNCTION_LANGUAGES = ["cpp", "java", "python", "javascript", "c"] as const;
export type FunctionLanguage = (typeof FUNCTION_LANGUAGES)[number];

export type FunctionStarters = Record<FunctionLanguage, string>;

export function isFunctionLanguage(languageKey: string): languageKey is FunctionLanguage {
  return (FUNCTION_LANGUAGES as readonly string[]).includes(languageKey);
}

/** True when the problem is a function problem: it has a signature. */
export function isFunctionProblem(problem: { signature?: FunctionSignature | null | undefined }): boolean {
  return Boolean(problem.signature);
}

/**
 * What "only a function" starts a new problem on, before the setter writes
 * their own in the Signature section. Every name passes the server's rules.
 */
export const DEFAULT_SIGNATURE: FunctionSignature = {
  name: "solve",
  params: [{ name: "nums", type: "int[]" }],
  returns: "int",
};

/* ── Types ───────────────────────────────────────────────────────────────── */

type FunctionScalarType = "int" | "long" | "double" | "bool" | "char" | "string";

const TYPE_SHAPE: Record<FunctionValueType, { element: FunctionValueType | null; base: FunctionScalarType; dims: 0 | 1 | 2 }> = {
  int: { element: null, base: "int", dims: 0 },
  long: { element: null, base: "long", dims: 0 },
  double: { element: null, base: "double", dims: 0 },
  bool: { element: null, base: "bool", dims: 0 },
  char: { element: null, base: "char", dims: 0 },
  string: { element: null, base: "string", dims: 0 },
  "int[]": { element: "int", base: "int", dims: 1 },
  "long[]": { element: "long", base: "long", dims: 1 },
  "double[]": { element: "double", base: "double", dims: 1 },
  "bool[]": { element: "bool", base: "bool", dims: 1 },
  "char[]": { element: "char", base: "char", dims: 1 },
  "string[]": { element: "string", base: "string", dims: 1 },
  "int[][]": { element: "int[]", base: "int", dims: 2 },
  "char[][]": { element: "char[]", base: "char", dims: 2 },
};

export function isFunctionValueType(type: unknown): type is FunctionValueType {
  return typeof type === "string" && (FUNCTION_VALUE_TYPES as readonly string[]).includes(type);
}

/** The element type of an array type, or `null` for a scalar. */
export function elementTypeOf(type: FunctionValueType): FunctionValueType | null {
  return TYPE_SHAPE[type].element;
}

/** 0 for a scalar, 1 for an array, 2 for a grid. */
export function dimensionsOf(type: FunctionValueType): 0 | 1 | 2 {
  return TYPE_SHAPE[type].dims;
}

/** True when a double sits anywhere in the type, so a tolerance means something. */
export function holdsDouble(type: FunctionValueType): boolean {
  return TYPE_SHAPE[type].base === "double";
}

/* ── The naming rules (a port of the server's `readSignature`) ─────────── */

/** camelCase, a letter first: a valid identifier in all five languages. */
export const SIGNATURE_NAME_PATTERN = /^[a-z][A-Za-z0-9]{0,30}$/;

/** The server's reserved names, word for word (`function-signature.ts`, `RESERVED_NAMES`). */
const RESERVED_NAMES = new Set([
  // C and C++
  "alignas", "alignof", "and", "asm", "auto", "bitand", "bitor", "bool", "break", "case", "catch", "char",
  "class", "compl", "concept", "const", "consteval", "constexpr", "constinit", "continue", "decltype",
  "default", "delete", "do", "double", "else", "enum", "explicit", "export", "extern", "false", "float",
  "for", "friend", "goto", "if", "inline", "int", "long", "mutable", "namespace", "new", "noexcept", "not",
  "nullptr", "operator", "or", "private", "protected", "public", "register", "requires", "restrict",
  "return", "short", "signed", "sizeof", "static", "struct", "switch", "template", "this", "throw", "true",
  "try", "typedef", "typeid", "typename", "union", "unsigned", "using", "virtual", "void", "volatile",
  "while", "xor", "std", "string", "vector", "main",
  // Java
  "abstract", "assert", "boolean", "byte", "extends", "final", "finally", "implements", "import",
  "instanceof", "interface", "native", "package", "strictfp", "super", "synchronized", "throws",
  "transient", "var", "yield", "record", "sealed", "permits", "null",
  // Python
  "async", "await", "def", "del", "elif", "except", "from", "global", "in", "is", "lambda", "nonlocal",
  "pass", "raise", "with", "self", "print", "input", "len", "list", "dict", "set", "str", "range", "type",
  "object", "id", "sum", "min", "max", "abs", "map", "filter", "sorted", "open", "exec", "eval", "compile",
  // JavaScript
  "arguments", "debugger", "function", "let", "of", "typeof", "undefined", "with", "eval", "require",
  "module", "exports", "process", "globalThis", "console",
  // C library names `progsity.h` brings in
  "exit", "malloc", "calloc", "realloc", "free", "printf", "scanf", "puts", "gets", "strlen", "strcmp",
  "strcpy", "memcpy", "memset", "qsort", "bsearch", "atoi", "atol", "pow", "sqrt", "floor", "ceil",
  "round", "log", "exp", "sin", "cos", "tan", "fabs", "fmax", "fmin", "rand", "srand", "time", "clock",
  "swap", "sort", "solution",
]);

/** Why one name cannot be used, or `null`. */
export type NameRefusal = "pattern" | "reserved";

export function nameRefusal(name: string): NameRefusal | null {
  if (!SIGNATURE_NAME_PATTERN.test(name)) return "pattern";
  if (RESERVED_NAMES.has(name)) return "reserved";
  return null;
}

/**
 * Why a signature cannot be saved, the server's `reason` with what it names,
 * or `null`. `index` is the 0-based parameter a refusal is about.
 */
export type SignatureRefusal =
  | { reason: "name"; why: NameRefusal }
  | { reason: "params"; why: "count" }
  | { reason: "params"; why: NameRefusal; index: number }
  | { reason: "params"; why: "duplicate"; index: number; name: string }
  | { reason: "params"; why: "c_size"; index: number; name: string }
  | { reason: "unordered" }
  | { reason: "tolerance"; why: "type" | "range" };

export function signatureRefusal(signature: {
  name: string;
  params: readonly FunctionSignatureParam[];
  returns: FunctionValueType;
  unordered?: boolean | undefined;
  tolerance?: number | null | undefined;
}): SignatureRefusal | null {
  const nameWhy = nameRefusal(signature.name);
  if (nameWhy) return { reason: "name", why: nameWhy };
  if (signature.params.length < 1 || signature.params.length > SIGNATURE_PARAMS_MAX) return { reason: "params", why: "count" };
  const seen = new Set<string>([signature.name]);
  const cNames = new Set<string>(["returnSize", "returnColumnSizes"]);
  for (const [index, param] of signature.params.entries()) {
    const why = nameRefusal(param.name);
    if (why) return { reason: "params", why, index };
    if (seen.has(param.name) || cNames.has(param.name)) {
      return { reason: "params", why: seen.has(param.name) ? "duplicate" : "c_size", index, name: param.name };
    }
    seen.add(param.name);
    const dims = dimensionsOf(param.type);
    if (dims >= 1) cNames.add(`${param.name}Size`);
    if (dims === 2) cNames.add(`${param.name}ColSize`);
  }
  // A later parameter named after an earlier one's size, as the server checks it.
  for (const [index, param] of signature.params.entries()) {
    if (cNames.has(param.name)) return { reason: "params", why: "c_size", index, name: param.name };
  }
  if (signature.unordered && dimensionsOf(signature.returns) === 0) return { reason: "unordered" };
  if (signature.tolerance !== undefined && signature.tolerance !== null) {
    if (!holdsDouble(signature.returns)) return { reason: "tolerance", why: "type" };
    if (!Number.isFinite(signature.tolerance) || signature.tolerance <= 0 || signature.tolerance > 1) {
      return { reason: "tolerance", why: "range" };
    }
  }
  return null;
}

/** A signature as the server stores it: `unordered` only when true, `tolerance` only when set. */
export function normaliseSignature(signature: {
  name: string;
  params: readonly FunctionSignatureParam[];
  returns: FunctionValueType;
  unordered?: boolean | undefined;
  tolerance?: number | null | undefined;
}): FunctionSignature {
  return {
    name: signature.name,
    params: signature.params.map((param) => ({ name: param.name, type: param.type })),
    returns: signature.returns,
    ...(signature.unordered === true && dimensionsOf(signature.returns) > 0 ? { unordered: true as const } : {}),
    ...(signature.tolerance !== undefined && signature.tolerance !== null && holdsDouble(signature.returns)
      ? { tolerance: signature.tolerance }
      : {}),
  };
}

/** The canonical string the server's lock compares: two signatures are one when these match. */
export function signatureKey(signature: FunctionSignature | null | undefined): string {
  return signature ? JSON.stringify(normaliseSignature(signature)) : "";
}

/* ── The starters (a port of the server's generators) ──────────────────── */

const C_SCALAR: Record<FunctionScalarType, string> = {
  int: "int",
  long: "long long",
  double: "double",
  bool: "bool",
  char: "char",
  string: "char*",
};

function cStarter(signature: FunctionSignature): string {
  const param = (name: string, type: FunctionValueType) => {
    const base = C_SCALAR[TYPE_SHAPE[type].base];
    const dims = dimensionsOf(type);
    if (dims === 0) return `${base} ${name}`;
    if (dims === 1) return `${base}* ${name}, int ${name}Size`;
    return `${base}** ${name}, int ${name}Size, int* ${name}ColSize`;
  };
  const dims = dimensionsOf(signature.returns);
  const base = C_SCALAR[TYPE_SHAPE[signature.returns].base];
  const returns = dims === 0 ? base : dims === 1 ? `${base}*` : `${base}**`;
  const extras = dims === 1 ? ["int* returnSize"] : dims === 2 ? ["int* returnSize", "int** returnColumnSizes"] : [];
  const params = [...signature.params.map((entry) => param(entry.name, entry.type)), ...extras];
  const note =
    dims === 1
      ? "/**\n * Return an array made with malloc, and set *returnSize to its length.\n */\n"
      : dims === 2
        ? "/**\n * Return an array of rows made with malloc, set *returnSize to the number\n * of rows and *returnColumnSizes to a malloc'd array of each row's length.\n */\n"
        : signature.returns === "string"
          ? `/**\n * Return a string made with malloc, ending in '${String.fromCharCode(92)}0'.\n */\n`
          : "";
  return `${note}${returns} ${signature.name}(${params.join(", ")}) {\n    \n}\n`;
}

const CPP_SCALAR: Record<FunctionScalarType, string> = { ...C_SCALAR, string: "string" };

function cppStarter(signature: FunctionSignature): string {
  const cppType = (type: FunctionValueType) => {
    const base = CPP_SCALAR[TYPE_SHAPE[type].base];
    const dims = dimensionsOf(type);
    return dims === 0 ? base : dims === 1 ? `vector<${base}>` : `vector<vector<${base}>>`;
  };
  const params = signature.params.map((param) =>
    dimensionsOf(param.type) === 0 ? `${cppType(param.type)} ${param.name}` : `${cppType(param.type)}& ${param.name}`,
  );
  return `class Solution {\npublic:\n    ${cppType(signature.returns)} ${signature.name}(${params.join(", ")}) {\n        \n    }\n};\n`;
}

const JAVA_SCALAR: Record<FunctionScalarType, string> = {
  int: "int",
  long: "long",
  double: "double",
  bool: "boolean",
  char: "char",
  string: "String",
};

function javaStarter(signature: FunctionSignature): string {
  const javaType = (type: FunctionValueType) => {
    const base = JAVA_SCALAR[TYPE_SHAPE[type].base];
    const dims = dimensionsOf(type);
    return dims === 0 ? base : dims === 1 ? `${base}[]` : `${base}[][]`;
  };
  const params = signature.params.map((param) => `${javaType(param.type)} ${param.name}`);
  return `class Solution {\n    public ${javaType(signature.returns)} ${signature.name}(${params.join(", ")}) {\n        \n    }\n}\n`;
}

const PYTHON_SCALAR: Record<FunctionScalarType, string> = {
  int: "int",
  long: "int",
  double: "float",
  bool: "bool",
  char: "str",
  string: "str",
};

function pythonStarter(signature: FunctionSignature): string {
  const pythonType = (type: FunctionValueType) => {
    const base = PYTHON_SCALAR[TYPE_SHAPE[type].base];
    const dims = dimensionsOf(type);
    return dims === 0 ? base : dims === 1 ? `List[${base}]` : `List[List[${base}]]`;
  };
  const params = signature.params.map((param) => `, ${param.name}: ${pythonType(param.type)}`).join("");
  return `class Solution:\n    def ${signature.name}(self${params}) -> ${pythonType(signature.returns)}:\n        \n`;
}

const JS_SCALAR: Record<FunctionScalarType, string> = {
  int: "number",
  long: "number",
  double: "number",
  bool: "boolean",
  char: "character",
  string: "string",
};

function javascriptStarter(signature: FunctionSignature): string {
  const jsDocType = (type: FunctionValueType) => {
    const base = JS_SCALAR[TYPE_SHAPE[type].base];
    const dims = dimensionsOf(type);
    return dims === 0 ? base : dims === 1 ? `${base}[]` : `${base}[][]`;
  };
  const doc = [
    "/**",
    ...signature.params.map((param) => ` * @param {${jsDocType(param.type)}} ${param.name}`),
    ` * @return {${jsDocType(signature.returns)}}`,
    " */",
  ].join("\n");
  const params = signature.params.map((param) => param.name).join(", ");
  return `${doc}\nvar ${signature.name} = function(${params}) {\n    \n};\n`;
}

const STARTERS: Record<FunctionLanguage, (signature: FunctionSignature) => string> = {
  c: cStarter,
  cpp: cppStarter,
  java: javaStarter,
  python: pythonStarter,
  javascript: javascriptStarter,
};

/** The starter a student's editor opens on, in one language: the server's text, character for character. */
export function generateStarter(signature: FunctionSignature, language: FunctionLanguage): string {
  return STARTERS[language](signature);
}

/* ── The server's refusals of a signature change ───────────────────────── */

export type SignatureLock =
  | { reason: "has_submissions"; submissionCount?: number }
  | { reason: "contest_mount" }
  /** `EX-FNC08`: a published or scheduled exam's coding task uses the problem. */
  | { reason: "exam_mount"; quizCount?: number }
  | { reason: "special_kind" }
  | { reason: "unsupported"; surface?: string };

/**
 * Reads `409 JUDGE_SIGNATURE_LOCKED`, `JUDGE_SIGNATURE_KIND_CONFLICT` or a
 * `JUDGE_PROBLEM_KIND_UNSUPPORTED` that names a function into the lock the
 * Signature section draws, or `null` for any other error.
 */
export function signatureLockFrom(error: unknown): SignatureLock | null {
  if (!error || typeof error !== "object") return null;
  const { code, details } = error as { code?: unknown; details?: unknown };
  const info = (details && typeof details === "object" ? details : {}) as Record<string, unknown>;
  if (code === "JUDGE_SIGNATURE_LOCKED") {
    if (info.reason === "contest_mount") return { reason: "contest_mount" };
    if (info.reason === "exam_mount") {
      return { reason: "exam_mount", ...(typeof info.quizCount === "number" ? { quizCount: info.quizCount } : {}) };
    }
    return {
      reason: "has_submissions",
      ...(typeof info.submissionCount === "number" ? { submissionCount: info.submissionCount } : {}),
    };
  }
  if (code === "JUDGE_SIGNATURE_KIND_CONFLICT") return { reason: "special_kind" };
  if (code === "JUDGE_PROBLEM_KIND_UNSUPPORTED" && info.kind === "function") {
    return { reason: "unsupported", ...(typeof info.surface === "string" ? { surface: info.surface } : {}) };
  }
  return null;
}
